import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    CadNextBootstrapView,
    UnitStateGroupEnum,
    UserAssignmentBundle,
    UserAssignmentResource,
} from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '@m43/core';
import { useApiClient } from '~/api-client';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useCurrentUser } from '../current-user/current-user';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { FR_EDIT_UNIT_PATH, FR_EVENT_ROOT_PATH } from '../router/constants';
import { AED, INCOMING_ASSIGNMENT } from './constants';
import { useFrState } from '../fr-state/use-fr-state';
import { useEnabledFeatures } from '../common/hooks/enabled-features';
import { logInfo } from '~/monitoring/log';
import { stableQueryKeys } from '~/common/helpers/stable-query-keys';

type UnitAssignmentContextValue = undefined | UserAssignmentBundle;
type UserAssignmentMessagePayload = {
    payload: UserAssignmentBundle;
};

const UnitAssignmentContext = createContext<UnitAssignmentContextValue>(undefined);

const UNIT_ASSIGNMENT_QUERY_CACHE_KEY = 'unit-assignment-bootstrap';

/**
 * Updates the query cache with the new user assignment bundle. We don't explicitly use the cache here,
 * but this is done to ensure anytime the app reads from cache due to a refetch or other reason, it has the latest data.
 */
const updateQueryCache = (userAssignmentBundle: UserAssignmentBundle) => {
    queryClient.setQueryData<CadNextBootstrapView>(stableQueryKeys.BOOTSTRAP_QUERY_KEY, (old) => {
        if (old === undefined) {
            return old;
        }

        return { ...old, userAssignmentBundle };
    });

    queryClient.setQueryData<{ userAssignmentBundle: UserAssignmentBundle }>(
        [UNIT_ASSIGNMENT_QUERY_CACHE_KEY],
        (old) => {
            if (old === undefined) {
                return old;
            }
            return { userAssignmentBundle };
        }
    );
};

export function UnitAssignmentProvider(props: { children: React.ReactNode }) {
    const { isFirstResponder } = useApplicationType();
    const { userAssignmentBundle: initialUserAssignmentBundle } = useBootstrapState();
    const modalControls = useFullscreenModalControls();
    const apiClient = useApiClient();
    const frState = useFrState();
    const enabledFeatures = useEnabledFeatures();

    const [unitAssignment, setUnitAssignment] = useState<UnitAssignmentContextValue>(
        initialUserAssignmentBundle
    );

    const currentUser = useCurrentUser();
    const lastAssignedEventNumberRef = useRef<undefined | number>(undefined);
    const wasLastAssignedEventAed = useRef<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const previousAssignedEvent = unitAssignment?.assignedEvent;

    const unitAssignmentBootstrapQuery = useQuery({
        queryKey: [UNIT_ASSIGNMENT_QUERY_CACHE_KEY],
        queryFn() {
            return apiClient
                .request<UserAssignmentResource.UserAssignmentBootstrap>({
                    method: 'POST',
                    url: 'user_assignment/bootstrap/',
                })
                .then((response) => response.data.data);
        },
        enabled: false,
    });

    const shouldShowIncomingEventModal = useCallback(
        (userAssignmentBundle: UserAssignmentBundle) => {
            const assignedEventId = userAssignmentBundle.assignedEvent?.eventId;

            const isNotCurrentAssignedEvent =
                assignedEventId && assignedEventId !== lastAssignedEventNumberRef.current;

            const isEventDispatched =
                userAssignmentBundle.unitStateData?.unitStateGroup ===
                    UnitStateGroupEnum.DISPATCHED.name ||
                userAssignmentBundle.unitStateData?.unitStateGroup ===
                    UnitStateGroupEnum.SYSTEM_DISPATCHED.name;

            return isNotCurrentAssignedEvent && isEventDispatched;
        },
        []
    );

    const processNewUserAssignmentBundle = useCallback(
        (userAssignmentBundle?: UserAssignmentBundle) => {
            if (!userAssignmentBundle) {
                return;
            }
            const assignedEvent = userAssignmentBundle.assignedEvent;
            const assignedEventId = assignedEvent?.eventId;
            const noLongerAssignedToEvent = !assignedEventId;
            const isIncomingEventAed = userAssignmentBundle.unitStateData?.abbreviation === AED;

            /**
             * Only navigate to event page in the following scenarios:
             * 1. User is required to answer the "Incoming Assignment" modal
             * 2. User is on the edit unit page
             *
             * General idea is navigate if we think it will not distrupt the user's current workflow.
             * Case 1 is an exception where we want to interrupt the flow.
             */
            let shouldNavigateToEvent = location.pathname === FR_EDIT_UNIT_PATH;

            if (assignedEventId && shouldShowIncomingEventModal(userAssignmentBundle)) {
                modalControls.open({
                    type: INCOMING_ASSIGNMENT,
                    props: {
                        eventId: assignedEventId,
                        isIncomingEventAed,
                    },
                });

                shouldNavigateToEvent = true;
            }

            const isStillDispatched =
                userAssignmentBundle.unitStateData?.unitStateGroup ===
                    UnitStateGroupEnum.DISPATCHED.name ||
                userAssignmentBundle.unitStateData?.unitStateGroup ===
                    UnitStateGroupEnum.SYSTEM_DISPATCHED.name;

            const shouldClearIncomingAssignmentModal =
                !wasLastAssignedEventAed.current &&
                ((assignedEventId && !isStillDispatched) || noLongerAssignedToEvent);

            if (shouldClearIncomingAssignmentModal) {
                modalControls.close();
            }

            if (previousAssignedEvent && noLongerAssignedToEvent) {
                frState.setWasHeldEventsModalShown(false);
            }

            lastAssignedEventNumberRef.current = assignedEventId;
            wasLastAssignedEventAed.current = isIncomingEventAed;

            //update app state
            setUnitAssignment(userAssignmentBundle);

            //update query cache
            updateQueryCache(userAssignmentBundle);

            if (assignedEventId && shouldNavigateToEvent) {
                navigate(`${FR_EVENT_ROOT_PATH}/${assignedEventId}`);
            }
        },
        [
            frState,
            location.pathname,
            modalControls,
            navigate,
            previousAssignedEvent,
            shouldShowIncomingEventModal,
        ]
    );

    useIntegratedStreams({
        streamNames: [
            enabledFeatures.rollOutFEIdAwareStreamsEnabled
                ? (`userAssignment|${currentUser.userId}` as 'userAssignment')
                : 'userAssignment',
        ],
        onMessage(message: UserAssignmentMessagePayload) {
            if (isFirstResponder) {
                processNewUserAssignmentBundle(message.payload);
            }
        },
        onReconnect() {
            if (isFirstResponder) {
                unitAssignmentBootstrapQuery.refetch().then((response) => {
                    // refetch silently fails in react query, so here we make sure it actually
                    // worked before updating the assigned unit
                    if (response.isSuccess) {
                        const userAssignmentBundle = response.data?.userAssignmentBundle;
                        processNewUserAssignmentBundle(userAssignmentBundle);
                    } else {
                        logInfo(
                            '[unit-assignment-context] Reconnect failed, assignment remains unchanged',
                            {
                                error: response.error,
                            }
                        );
                    }
                });
            }

            return Promise.resolve();
        },
        query: unitAssignmentBootstrapQuery,
    });

    return (
        <UnitAssignmentContext.Provider value={unitAssignment}>
            {props.children}
        </UnitAssignmentContext.Provider>
    );
}

export function useUnitAssignment() {
    return useContext(UnitAssignmentContext);
}
